import { useCallback } from 'react';
import cn from 'classnames';

import { Icons } from '@icons/icons';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IMultiDataTableFilter } from '@interfaces/components/table';

import colors from '@constants/colors';

import MultiDataFilterForm from '../MultiDataFilterForm';

import styles from '../MultiDataFilter.module.scss';

interface IMultiDataFilterChipProps {
	index: number;
	name: string;
	subName: string;
	filterProps: IMultiDataTableFilter['filterProps'];
	filter: Record<string, TSelectOption<string> | null>;
	openIdx: number | null;
	setOpenIdx: (idx: number | null) => void;
	setSelectedFilters: IMultiDataTableFilter['setSelectedFilters'];
	clearSelectionAtIndex: (index: number) => void;
}

const MultiDataFilterChip = ({
	index,
	name,
	subName,
	filterProps,
	filter,
	openIdx,
	setOpenIdx,
	setSelectedFilters,
	clearSelectionAtIndex,
}: IMultiDataFilterChipProps) => {
	const option = filter?.[name];
	const subOption = filter?.[subName];
	const isOptionSelected = !!option?.value && !!subOption?.value;

	const toggleOpen = useCallback(() => {
		setOpenIdx(openIdx === index ? null : index);
	}, [openIdx, index, setOpenIdx]);

	const onSubmit = useCallback((optionSel: TSelectOption<string>, subOptionSel: TSelectOption<string>) => {
		setSelectedFilters((prev) => {
			const updatedFilters = prev.map((item, idx) => {
				if (idx === index) {
					return { ...item, [name]: optionSel, [subName]: subOptionSel };
				}

				return item;
			});

			return prev.length <= filterProps.options.length
				? [...updatedFilters, { [name]: null, [subName]: null }]
				: updatedFilters;
		});
	}, [filterProps.options.length, index, name, setSelectedFilters, subName]);

	const onActionClick = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();

		if (isOptionSelected) {
			clearSelectionAtIndex(index);
		} else {
			toggleOpen();
		}
	}, [clearSelectionAtIndex, index, isOptionSelected, toggleOpen]);

	const onWrapperClick = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();

		toggleOpen();
	}, [toggleOpen]);

	const onFilterActionClick = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	const onClose = () => {
		setOpenIdx(null);
	};

	const {
		placeholder,
		subPlaceholder,
		minWidth,
	} = filterProps;

	const filterLabel = isOptionSelected
		? `${placeholder}: ${option?.value} & ${subPlaceholder}: ${subOption?.value}`
		: `${placeholder} & ${subPlaceholder}`;

	const filterClassNames = cn(styles.filter, {
		[styles.filterActive]: isOptionSelected,
	});

	return (
		<div
			className={filterClassNames}
			onMouseDown={onWrapperClick}
			style={{ minWidth }}
		>
			<div className={styles.filterWrapper}>
				<div className={styles.filterLabel}>{filterLabel}</div>
				<div
					className={styles.filterAction}
					onClick={onActionClick}
					onMouseDown={onFilterActionClick}
				>
					{isOptionSelected
						? Icons.clearMultiselect({ color: colors.white })
						: openIdx === index
							? Icons.chevronUp()
							: Icons.chevronDown()
					}
				</div>
			</div>
			<MultiDataFilterForm
				filterProps={filterProps}
				isVisibleForm={openIdx === index}
				onClick={onSubmit}
				onClose={onClose}
				option={option}
				subOption={subOption}
			/>
		</div>
	);
};

export default MultiDataFilterChip;
